import { formatTimestamp } from '@fit-editor/fit-core'
import type { TimeDisplayMode } from '@fit-editor/fit-core'

import {
  distanceUnitLabel,
  elevationUnitLabel,
  formatCalories,
  formatDistanceMeters,
  formatDurationSeconds,
  formatElevationMeters,
  formatHeartRate,
  formatSpeedMetersPerSecond,
  metersToDistanceUnit,
  metersToElevationUnit,
} from '@/lib/format'
import type { UnitSystem } from '@/lib/format'

/** Display formatting for the FIT data inspector. Presentation-only. */

export const EMPTY_VALUE = '—'

const SEMICIRCLES_TO_DEGREES = 180 / 2 ** 31

export function semicirclesToDegrees(semicircles: number): number {
  return semicircles * SEMICIRCLES_TO_DEGREES
}

/** A number without float noise: at most `maxDecimals` decimals, trailing zeros trimmed. */
export function formatPlainNumber(value: number, maxDecimals = 6): string {
  if (!Number.isFinite(value)) return String(value)
  if (Number.isInteger(value)) return String(value)
  return String(Number(value.toFixed(maxDecimals)))
}

/**
 * A field value as the SDK decoded it (scaled, enum names, `Date`s), with
 * no unit conversion -- the "All messages" view, where units are shown in
 * their own column.
 */
export function formatDecodedValue(value: unknown, timeMode: TimeDisplayMode): string {
  if (value == null) return EMPTY_VALUE
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? formatTimestamp(value, timeMode) : 'Invalid date'
  }
  if (typeof value === 'number') return formatPlainNumber(value)
  if (typeof value === 'string') return value.length > 0 ? value : EMPTY_VALUE
  if (typeof value === 'boolean' || typeof value === 'bigint') return String(value)
  if (Array.isArray(value)) {
    if (value.length > 64) {
      return `[${value
        .slice(0, 64)
        .map((v) => formatDecodedValue(v, timeMode))
        .join(', ')}, … ${value.length - 64} more]`
    }
    return `[${value.map((v) => formatDecodedValue(v, timeMode)).join(', ')}]`
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/** A raw (unscaled) value for the "Raw" column. */
export function formatRawValue(raw: unknown): string {
  if (raw == null) return ''
  if (Array.isArray(raw))
    return `[${raw.map((v) => (v == null ? EMPTY_VALUE : String(v))).join(', ')}]`
  return String(raw)
}

export interface FriendlyFormatOptions {
  /**
   * Per-sample precision (the records table): distances/altitudes in
   * metres with a decimal and durations in seconds, rather than the
   * rounded km / h:mm:ss summary style used for sessions and laps.
   */
  precise?: boolean
  /**
   * Display unit system for distances, elevations and speeds (default
   * metric). Display only -- the decoded value itself is never converted.
   */
  unitSystem?: UnitSystem
}

/**
 * A field value formatted for a human-readable table, using the app's
 * shared formatters (`lib/format.ts`) where the field's profile units make
 * the conversion unambiguous, and the display-time helper for instants.
 * Anything not recognized falls back to the decoded value plus its units.
 */
export function formatFriendlyValue(
  fieldName: string,
  value: unknown,
  units: string,
  timeMode: TimeDisplayMode,
  options: FriendlyFormatOptions = {},
): string {
  if (value == null) return EMPTY_VALUE
  if (typeof value !== 'number') return formatDecodedValue(value, timeMode)
  if (!Number.isFinite(value)) return String(value)
  const unitSystem = options.unitSystem ?? 'metric'

  switch (units) {
    case 'semicircles':
      return `${semicirclesToDegrees(value).toFixed(6)}°`
    case 'm/s':
      return formatSpeedMetersPerSecond(value, unitSystem)
    case 'bpm':
      return formatHeartRate(value)
    case 'kcal':
      return formatCalories(value)
    case 'm': {
      const isDistance = /distance/i.test(fieldName)
      if (options.precise) {
        if (unitSystem === 'metric') return `${formatPlainNumber(value, 1)} m`
        // Per-sample precision in imperial: miles to ~1.6 m, feet to 0.1 ft.
        return isDistance
          ? `${formatPlainNumber(metersToDistanceUnit(value, unitSystem), 3)} ${distanceUnitLabel(unitSystem)}`
          : `${formatPlainNumber(metersToElevationUnit(value, unitSystem), 1)} ${elevationUnitLabel(unitSystem)}`
      }
      return isDistance
        ? formatDistanceMeters(value, unitSystem)
        : formatElevationMeters(value, unitSystem)
    }
    case 's':
      if (options.precise) return `${formatPlainNumber(value, 3)} s`
      return formatDurationSeconds(value)
    case '':
      return formatPlainNumber(value)
    default:
      return `${formatPlainNumber(value)} ${units}`
  }
}
